import axiosInstance from "config/AxiosConfig";
import { BaseResponse } from "domain/payload/response/BaseResponseDto";
import { 
    TribeListResp,
    TribeDetailResp,
    TribeProjectListResp,
    TribeUserListResp
} from "domain/payload/response/tribe/TribeResp";
import { 
    TribeCreateReq,
    TribeUpdateReq,
    AddProjectWithUsersReq, 
    RemoveUserFromTribeProjectReq, 
    DeleteProjectFromTribeReq 
} from "domain/payload/request/tribe/TribeReq";

const TribeService = () => {
  const getTribeList = async (
    token: string,
    page?: number,
    size?: number,
    search?: string | null
  ): Promise<BaseResponse<TribeListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/tribes/list`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, size, search },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const getTribeDetail = async (
    token: string,
    id: string
  ): Promise<BaseResponse<TribeDetailResp>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/tribes/detail`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { id },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const createTribe = async (
    token: string,
    data: TribeCreateReq
  ): Promise<BaseResponse<TribeDetailResp>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/tribes/create`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const updateTribe = async (
    token: string,
    data: TribeUpdateReq
  ): Promise<BaseResponse<TribeDetailResp>> => {
    try {
      const response = await axiosInstance.put(
        `/v1/legacy/cms/tribes/update`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const deleteTribe = async (
    token: string,
    id: string
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.delete(`/v1/legacy/cms/tribes/delete`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { id },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const uploadTribeLogo = async (
    token: string,
    tribeId: string,
    file: File
  ): Promise<BaseResponse<{ url: string }>> => {
    try {
      const formData = new FormData();
      formData.append("tribeId", tribeId);
      formData.append("file", file);

      const response = await axiosInstance.post(
        `/v1/legacy/cms/tribes/upload-logo`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const addProjectWithUsers = async (
    token: string,
    data: AddProjectWithUsersReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.post(
        `/v1/legacy/cms/tribes/add-project-with-users`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const removeUserFromTribeProject = async (
    token: string,
    data: RemoveUserFromTribeProjectReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.delete(
        `/v1/legacy/cms/tribes/remove-user-from-project`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: data,
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const deleteProjectFromTribe = async (
    token: string,
    data: DeleteProjectFromTribeReq
  ): Promise<BaseResponse<null>> => {
    try {
      const response = await axiosInstance.delete(
        `/v1/legacy/cms/tribes/delete-project`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: data,
        }
      );
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const getTribeProjects = async (
    token: string,
    tribeId: string,
    page: number = 1,
    size: number = 10,
    search?: string | null
  ): Promise<BaseResponse<TribeProjectListResp[]>> => {
    try {
      const response = await axiosInstance.get(`/v1/legacy/cms/tribes/${tribeId}/projects`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { 
          page,
          size,
          search: search || undefined
        },
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  const getTribeUsers = async (
    token: string,
    tribeId: string,
    projectId?: string,
    page: number = 1,
    size: number = 10,
    search?: string | null
  ): Promise<BaseResponse<TribeUserListResp[]>> => {
    try {
      const params: any = { 
        page,
        size,
        search: search || undefined
      };
      if (projectId) {
        params.projectId = projectId;
      }
      
      const response = await axiosInstance.get(`/v1/legacy/cms/tribes/${tribeId}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      return response.data;
    } catch (error: any) {
      throw error;
    }
  };

  return {
    getTribeList,
    getTribeDetail,
    createTribe,
    updateTribe,
    deleteTribe,
    uploadTribeLogo,
    addProjectWithUsers,
    removeUserFromTribeProject,
    deleteProjectFromTribe,
    getTribeProjects,
    getTribeUsers,    
  };
};

export default TribeService;